import { Capacitor } from '@capacitor/core';
import { ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getStadiumInfo } from '../../apis/getStadiumInfo';
import { getUserInfo } from '../../apis/getUserInfo';
import Toast from '../../components/Toast/Toast';
import { LANG_TEXT } from '../../constants/language';
import { ROLES } from '../../consts/language';
import Auth, { initAuth } from '../../libs/auth-wrapper';

interface AuthContextValue {
  user: any;
  updateUser: (user: any) => void;
  getUserUid: () => void;
  getUserEmail: (user: any) => void;
  logoutUser: () => void;
  setUserInfo: any;
  userInfo: any;
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  updateUser: () => {},
  getUserUid: () => {},
  getUserEmail: () => {},
  logoutUser: () => {},
  setUserInfo: () => {},
  userInfo: null,
  loading: false,
});

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [openToast, setOpenToast] = useState(false);
  const { t } = useTranslation();

  const toast = useRef<{ type: 'error' | 'success'; content: string }>({
    type: 'success',
    content: '',
  });

  useEffect(() => {
    if (!user) return;

    const fetchUserInfo = async () => {
      const userId = getUserUid();
      if (!userId) return;

      try {
        const stadium = await getStadiumInfo(userId);

        if (stadium.stadium_id === 'no stadium') {
          const user = await getUserInfo(userId);

          if (user.restaurant_id === 'no restaurant') {
            toast.current = {
              content: t(LANG_TEXT.LOGIN_FAIL_MESSAGE),
              type: 'error',
            };
            setOpenToast(true);
            await logoutUser();
          } else {
            setUserInfo({ ...user, role: ROLES.STAFF });
          }
        } else {
          const role = stadium.role === 'SUPER_ADMIN' ? ROLES.SUPER_ADMIN : ROLES.HEADQUARTER;
          setUserInfo({ ...stadium, role });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, [user]);

  const getUserUid = () => {
    if (!user || !user.uid) return null;
    return user.uid;
  };

  const updateUser = (user: any) => {
    if (!user || !user.uid) {
      setLoading(false);
      Auth.signOut();
      localStorage.removeItem('idToken');
      clearUserLoginState();
      return;
    }
    setLoading(true);
    setUser(user);
  };

  const logoutUser = async () => {
    await Auth.signOut();
    localStorage.removeItem('idToken');
    clearUserLoginState();
  };

  const clearUserLoginState = () => {
    setUser(null);
    setUserInfo(null);
    setLoading(false);
  };

  const getUserEmail = (user: any) => {
    if (user?.email) return user.email;
    return '';
  };

  useEffect(() => {
    let unsub: any;
    let mounted = true;

    (async () => {
      setLoading(true);

      if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
        await initAuth();
        if (!mounted) return;
        unsub = Auth.onAuthStateChanged(updateUser);
        return;
      }

      try {
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        if (!mounted) return;
        unsub = auth.onAuthStateChanged(updateUser);
      } catch (e) {
        await initAuth();
        if (!mounted) return;
        unsub = Auth.onAuthStateChanged(updateUser);
      }
    })();

    return () => {
      mounted = false;
      if (typeof unsub === 'function') unsub();
    };
  }, []);

  return (
    <>
      <Toast content={toast.current?.content} variant={toast.current?.type} open={openToast} onClose={() => setOpenToast(false)} />
      <AuthContext.Provider
        value={{
          user,
          updateUser,
          getUserUid,
          getUserEmail,
          logoutUser,
          setUserInfo,
          userInfo,
          loading,
        }}
      >
        {children}
      </AuthContext.Provider>
    </>
  );
};

const useAuthContext = () => useContext(AuthContext);

export { AuthProvider, useAuthContext };
